import React, { useEffect, useState } from "react";
import { apiRequest } from "../../utils/api";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";

function NumbersUpload() {
  const [files, setFiles] = useState([]);
  const [file, setFile] = useState();
  const [numbers, setNumbers] = useState([]);

  // States for Custom TXT Importer
  const [txtFile, setTxtFile] = useState(null);
  const [txtFileType, setTxtFileType] = useState("new_release");

  const [zipFile, setZipFile] = useState(null);
  const [zipLocation, setZipLocation] = useState("");
  const [localZips, setLocalZips] = useState([]);

  async function deleteNumber(file) {
    try {
      const res = await apiRequest.delete(
        "/upload-numbers/delete-file/" + file?.file_id
      );
      if (res.data.status === 200 || res.status === 200) {
        setNumbers([]);
        getFiles();
        Swal.fire("Success", "File record and resources removed", "success");
      } else {
        console.log(res.data.message);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function download(file) {
    try {
      const res = await apiRequest.get(
        "/upload-numbers/single/" + file?.file_id,
        { responseType: "blob" }
      );

      // If the server accidentally sent JSON error, convert and log it
      const contentType = res.headers["content-type"];
      if (contentType && contentType.includes("application/json")) {
        const text = await res.data.text();
        console.error("❌ Server Error:", text);
        return;
      }

      // Extract filename from headers or fallback
      const contentDisposition = res.headers["content-disposition"];
      let fileName = `file-${file?.file_id}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match && match[1]) fileName = match[1];
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      console.log("✅ File downloaded:", fileName);
    } catch (error) {
      console.error("❌ Download failed:", error);
    }
  }

  async function getFiles() {
    try {
      let res = await apiRequest.get("/upload-numbers/all");
      if (res.data.status === 200 || res.status === 200) {
        const data = res.data?.message || res.data || [];
        setFiles(data);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function getLocalZips() {
    try {
      console.log("[getLocalZips] Requesting /dvla-data/list-zips...");
      const res = await apiRequest.get("/dvla-data/list-zips");
      console.log("[getLocalZips] Response received:", res.status, res.data);
      if (res.status === 200 || res.data?.status === 200) {
        const data = res.data?.message || res.data || [];
        setLocalZips(data);
      }
    } catch (error) {
      console.error("[getLocalZips] Failed to list local zip files:", error);
    }
  }

  useEffect(() => {
    getFiles();
    getLocalZips();
  }, []);

  const columns = [
    {
      name: "S.No",
      selector: (row, index) => <>{index + 1}</>,
      width: "80px",
    },
    {
      name: "File Path / Location",
      selector: (row) => row?.file_location || "system",
      sortable: true,
    },
    {
      name: "Total Numbers",
      selector: (row) => row?.total_numbers,
      width: "140px",
    },
    {
      name: "Added",
      selector: (row) => row?.added_numbers || 0,
      width: "100px",
    },
    {
      name: "File Status",
      selector: (row) => (
        <span
          className={`badge ${
            row?.file_status === "deleted" ? "bg-danger" : "bg-success"
          }`}
        >
          {row?.file_status}
        </span>
      ),
      width: "120px",
    },
    {
      name: "Uploaded On",
      selector: (row) =>
        row?.created_at ? new Date(row.created_at).toLocaleString() : "-",
      sortable: true,
    },
    {
      name: "Action",
      selector: (row) => (
        <>
          <button
            disabled={row?.file_status === "deleted"}
            onClick={() => download(row)}
            className="btn btn-sm btn-success me-2"
          >
            Download
          </button>
          <button
            disabled={row?.file_status === "deleted"}
            onClick={() => deleteNumber(row)}
            className="btn btn-sm btn-danger"
          >
            Delete
          </button>
        </>
      ),
      width: "180px",
    },
  ];

  async function uploadFile() {
    if (!file) {
      Swal.fire("Error", "Please choose an Excel/CSV file first", "error");
      return;
    }

    const formData = new FormData();
    formData.append("file", file[0]); // match single file from FileList

    try {
      Swal.showLoading();
      const res = await apiRequest.post("/upload-numbers/add", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      Swal.close();
      if (res.data.status === 200 || res.status === 200) {
        Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Excel Processed Successfully",
          showConfirmButton: false,
          timer: 1500,
        });
        getFiles();
      } else {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Something went wrong!",
        });
      }
    } catch (error) {
      Swal.close();
      console.error("Upload failed:", error.response?.data || error.message);
      Swal.fire("Error", error.message, "error");
    }
  }

  async function uploadTxtFile() {
    if (!txtFile) {
      Swal.fire("Error", "Please select a .txt file first", "error");
      return;
    }

    const formData = new FormData();
    formData.append("file", txtFile);

    try {
      Swal.fire({
        title: `Processing ${txtFileType.replace("_", " ")} registrations...`,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const res = await apiRequest.post(
        `/upload-numbers/add-txt?file_type=${txtFileType}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      Swal.close();
      if (res.data.status === 200 || res.status === 200) {
        Swal.fire({
          icon: "success",
          title: "Import Successful",
          text: res.data.message || "TXT file processed successfully directly into the active database!",
        });
        getFiles();
      } else {
        Swal.fire("Error", res.data?.message || "Failed to process TXT file", "error");
      }
    } catch (error) {
      Swal.close();
      console.error(error);
      Swal.fire("Error", error.response?.data?.message || error.message, "error");
    }
  }

  async function importZipFile() {
    if (!zipFile && !zipLocation.trim()) {
      Swal.fire("Error", "Please upload a ZIP file or select a backend ZIP file", "error");
      return;
    }

    const formData = new FormData();
    if (zipFile) {
      formData.append("file", zipFile);
    } else {
      formData.append("filePath", zipLocation);
    }

    try {
      Swal.fire({
        title: "Triggering Ingestion...",
        text: "Please wait while the ZIP ingestion starts in the background.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const res = await apiRequest.post("/dvla-data/import-zip", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      Swal.close();

      if (res.status === 202 || res.data?.status === 202) {
        Swal.fire({
          icon: "success",
          title: "ZIP Import Triggered",
          text: "The background snapshot extraction pipeline is running. You can monitor progress under the DVLA page!",
        });
        getFiles();
        getLocalZips(); // refresh the zip files list
      } else {
        Swal.fire("Error", res.data?.message || "Failed to trigger ZIP import", "error");
      }
    } catch (error) {
      Swal.close();
      console.error(error);
      Swal.fire("Error", error.response?.data?.message || error.message, "error");
    }
  }

  return (
    <>
      <div className="main-content">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0">Registration Ingestion Panel</h2>
          <Link className="btn btn-danger" to={`/delete-number`}>
            Delete Number Page
          </Link>
        </div>

        <div className="row">
          {/* Card 1: Standard Excel/CSV */}
          <div className="col-lg-4 col-md-6 mb-4">
            <div className="card h-100 shadow-sm border-0">
              <div className="card-header bg-primary text-white py-3">
                <h5 className="card-title mb-0">Excel/CSV Bulk Importer</h5>
              </div>
              <div className="card-body d-flex flex-column justify-content-between">
                <div>
                  <p className="text-muted small">
                    Import large bulk records formatted in Excel spreadsheets (`.csv`, `.xlsx`).
                  </p>
                  <div className="mb-3">
                    <label className="form-label small fw-bold">Select Spreadsheet File</label>
                    <input
                      type="file"
                      className="form-control"
                      accept=".xlsx, .csv"
                      onChange={(e) => setFile(e.target.files)}
                    />
                  </div>
                </div>
                <button onClick={uploadFile} className="btn btn-primary w-100 mt-3">
                  Upload Excel
                </button>
              </div>
            </div>
          </div>

          {/* Card 2: Custom TXT Snapshot Importer */}
          <div className="col-lg-4 col-md-6 mb-4">
            <div className="card h-100 shadow-sm border-0">
              <div className="card-header bg-dark text-white py-3">
                <h5 className="card-title mb-0">Custom TXT File Importer</h5>
              </div>
              <div className="card-body d-flex flex-column justify-content-between">
                <div>
                  <p className="text-muted small">
                    Upload simple text files (`.txt`). Parses and uploads registrations directly into active live tables.
                  </p>
                  <div className="mb-3">
                    <label className="form-label small fw-bold">Select TXT File</label>
                    <input
                      type="file"
                      className="form-control"
                      accept=".txt"
                      onChange={(e) => setTxtFile(e.target.files[0])}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-bold">Select File Type</label>
                    <select
                      className="form-select"
                      value={txtFileType}
                      onChange={(e) => setTxtFileType(e.target.value)}
                    >
                      <option value="new_style">New Style</option>
                      <option value="prefix">Prefix</option>
                      <option value="old">Old (Dateless)</option>
                      <option value="open_sale">Open Sale</option>
                      <option value="deleted">Deleted</option>
                      <option value="new_release">New Release</option>
                    </select>
                    <div className="form-text text-muted small mt-1">
                      Prices are automatically read directly from the file columns. No manual pricing is required.
                    </div>
                  </div>
                </div>
                <button onClick={uploadTxtFile} className="btn btn-dark w-100 mt-3">
                  Upload & Ingest Snapshot
                </button>
              </div>
            </div>
          </div>

          {/* Card 3: ZIP Snapshot Importer */}
          <div className="col-lg-4 col-md-6 mb-4">
            <div className="card h-100 shadow-sm border-0">
              <div className="card-header bg-success text-white py-3">
                <h5 className="card-title mb-0">ZIP Snapshot Importer</h5>
              </div>
              <div className="card-body d-flex flex-column justify-content-between">
                <div>
                  <p className="text-muted small">
                    Upload a DVLA ZIP archive directly, or choose one of the ZIP snapshots located in `uploads/dvla`.
                  </p>
                  <div className="mb-3">
                    <label className="form-label small fw-bold">Option A: Upload ZIP File</label>
                    <input
                      type="file"
                      className="form-control"
                      accept=".zip"
                      onChange={(e) => {
                        setZipFile(e.target.files[0]);
                        setZipLocation(""); // clear other option
                      }}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-bold">Option B: Select Backend ZIP File</label>
                    <select
                      className="form-select"
                      value={zipLocation}
                      onChange={(e) => {
                        setZipLocation(e.target.value);
                        setZipFile(null); // clear other option
                      }}
                    >
                      <option value="">-- Choose a local backend ZIP --</option>
                      {localZips.map((z, idx) => (
                        <option key={idx} value={z.path}>
                          {z.name} ({(z.size / 1024 / 1024).toFixed(2)} MB)
                        </option>
                      ))}
                    </select>
                    {localZips.length === 0 && (
                      <div className="form-text text-muted small mt-1">
                        No zip files found in the `uploads/dvla` folder.
                      </div>
                    )}
                  </div>
                </div>
                <button onClick={importZipFile} className="btn btn-success w-100 mt-3">
                  Run ZIP Snapshot Pipeline
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* History / Records Log Table */}
        <div className="card shadow-sm border-0 mt-3">
          <div className="card-header bg-white py-3 border-bottom">
            <h5 className="mb-0 text-secondary fw-bold">Ingestion Upload History Log</h5>
          </div>
          <div className="card-body p-0">
            <DataTable columns={columns} data={files} pagination highlightOnHover />
          </div>
        </div>
      </div>
    </>
  );
}

export default NumbersUpload;
